import { Trans } from "@lingui/macro";
import { DarkMode } from "@winamp/icons/DarkMode.js";
import { LightMode } from "@winamp/icons/LightMode.js";
import { Label } from "@winamp/ui/components/Fieldset/Label.js";
import { Switch } from "@winamp/ui/components/Switch/Switch.js";
import { SwitchField } from "@winamp/ui/components/Switch/SwitchField.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { parseWithValibot } from "conform-to-valibot";
import type { ComponentProps } from "react";
import { useFetcher, useFetchers } from "react-router";
import type { action } from "~/root";
import { useRequestInfo } from "../request/request-info";
import { ThemeFormSchema } from "./form-schema";
import type { Theme } from "./theme.server";

export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find(
    (f) => f.formAction === "/" && f.key === "theme-switch",
  );

  if (themeFetcher?.formData) {
    const submission = parseWithValibot(themeFetcher.formData, {
      schema: ThemeFormSchema,
    });

    if (submission.status === "success") {
      return submission.value.theme;
    }
  }
}

function getActiveMode(mode: "system" | Theme) {
  if (mode !== "system") return mode;

  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeSwitch({
  withLabel = false,
  className,
  ...props
}: ComponentProps<typeof Switch> & {
  withLabel?: boolean;
}) {
  const { mode, setMode } = useThemeSwitch();

  return (
    <SwitchField
      className={classNames("flex items-center w-fit gap-1.5", className)}
    >
      {withLabel && (
        <Label className="text-xs sm:hidden">
          {mode === "dark" ? (
            <DarkMode className="size-6 text-secondary" />
          ) : (
            <LightMode className="size-6 text-secondary" />
          )}
        </Label>
      )}
      {withLabel && (
        <Label className="text-xs max-sm:hidden">
          {mode === "dark" ? <Trans>Dark</Trans> : <Trans>Light</Trans>}
        </Label>
      )}
      <Switch
        defaultChecked={mode === "dark"}
        onChange={(isDark) => setMode(isDark ? "dark" : "light")}
        {...props}
      />
    </SwitchField>
  );
}

export function useThemeSwitch() {
  const { userPrefs } = useRequestInfo();
  const fetcher = useFetcher<typeof action>({ key: "theme-switch" });

  const optimisticMode = useOptimisticThemeMode();
  const mode = getActiveMode(optimisticMode ?? userPrefs.theme ?? "system");

  const setMode = (theme: "system" | Theme) => {
    fetcher.submit(
      {
        intent: "theme",
        theme,
      },
      {
        method: "POST",
        action: "/",
      },
    );
  };

  return {
    mode,
    setMode,
  };
}
